<script setup lang="ts">
import { computed, reactive, ref, defineExpose } from 'vue'

interface DataRow {
  data: Record<string, any>
  errors?: Record<string, string[]>
}

const isOpened = ref(false)
const itemInfos = reactive({
  nom: '',
  prenom: '',
  date_naissance: '',
  telephone: '',
  email: '',
  salaire: '',
  quotite_cessible_montant: '',
  quotite_cessible_pourcentage: ''
})
const itemErrors = ref<Record<string, string[]>>({})

function open(dataRow: DataRow) {
  Object.assign(itemInfos, dataRow.data || {})
  itemErrors.value = dataRow.errors || {}
  isOpened.value = true
}

function close() {
  isOpened.value = false
}

const errorsText = computed(() => {
  const values = Object.values(itemErrors.value || {})
  if (values.length === 0) {
    return ''
  }
  return values.flat().join(' ')
})

defineExpose({
  open,
  close
})
</script>

<template>
  <div v-if="isOpened" class="modal-backdrop">
    <div class="modal-card">
      <header class="modal-header">
        <h4 class="font-semibold mb-0">Informations compte</h4>
        <button class="btn sm white2" @click="close">
          <img src="/public/icons/icon-close.svg" class="btn-icon" alt="" />
        </button>
      </header>

      <div class="modal-body">
        <div v-if="errorsText" class="alert alert-danger mb-4">
          {{ errorsText }}
        </div>
        <h5 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
          <img src="/public/icons/icon-info-perso.svg" class="size-base" alt="" />
          Informations personnelles
        </h5>
        <div class="item-row">
          <div class="label">Nom</div>
          <div class="item-info">{{ itemInfos.nom || '-' }}</div>
        </div>
        <div class="item-row">
          <div class="label">Prénoms</div>
          <div class="item-info">{{ itemInfos.prenom || '-' }}</div>
        </div>
        <div class="item-row">
          <div class="label">Date naissance</div>
          <div class="item-info">{{ itemInfos.date_naissance || '-' }}</div>
        </div>
        <div class="item-row">
          <div class="label">Téléphone</div>
          <div class="item-info">{{ itemInfos.telephone || '-' }}</div>
        </div>
        <div class="item-row">
          <div class="label">Adresse Email</div>
          <div class="item-info">{{ itemInfos.email || '-' }}</div>
        </div>

        <h5 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
          <img src="/public/icons/icon-engagement.svg" class="size-base" alt="" />
          Engagement
        </h5>
        <div class="item-row">
          <div class="label">Salaire brut</div>
          <div class="item-info">{{ itemInfos.salaire || '-' }}</div>
        </div>
        <div class="item-row">
          <div class="label">Quotité cessible</div>
          <div class="item-info">
            {{ itemInfos.quotite_cessible_montant }} ({{ itemInfos.quotite_cessible_pourcentage }}%)
          </div>
        </div>
      </div>

      <footer class="modal-footer">
        <button class="btn-ultra white2" style="justify-content: center" @click="close">Fermer</button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-card {
  background: #fff;
  border-radius: 20px;
  width: min(640px, 90%);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.modal-header,
.modal-footer {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: none;
}

.modal-body {
  padding: 1.5rem 2rem;
  overflow-y: auto;
}

.label {
  color: rgba(var(--color-black-rgb), 0.4);
  font-weight: 600;
  font-size: 0.85rem;
}

.item-row {
  margin-bottom: 0.75rem;
}

.item-info {
  color: rgba(var(--color-black-rgb), 0.8);
  font-weight: 600;
}
</style>

